import { Check, ChevronLeft, Dumbbell, Lock, MapPin, Plus, RotateCw, TrendingUp } from "lucide-react";
import type { CSSProperties } from "react";

import { BrandMark } from "@/features/site/components/brand-mark";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/**
 * The phone screens of `/features`, at an iPhone's 402 pt width and iOS type sizes: the coach app
 * in its own colour, and the client's phone in the athlete app's lime, so the two read as the two
 * sides of one session. Sample people and numbers. Each screen starts under the Dynamic Island.
 */

const screen = "absolute inset-0 flex flex-col pt-[62px]";
const well = "bg-foreground/[0.06]";

/**
 * The athlete app's lime. Set on the screen's content rather than the phone, because the screen's
 * own `dark` class would set the coach app's colour back.
 */
const athleteTheme = {
  "--primary": "oklch(0.9 0.2 122)",
  "--primary-foreground": "oklch(0.2 0.02 120)",
} as CSSProperties;

export const kilos = (value: number) =>
  new Intl.NumberFormat(getLocale(), { maximumFractionDigits: 1 }).format(value);

/** A person without a photo: their initials on a soft wash of the primary colour. */
export const Initials = ({ name, className }: { name: string; className?: string }) => (
  <span
    aria-hidden
    className={cn(
      "grid shrink-0 place-items-center bg-linear-to-br from-primary/25 to-primary/10 font-semibold text-primary",
      className,
    )}
  >
    {name
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")}
  </span>
);

const LargeTitle = ({ kicker, title }: { kicker: string; title: string }) => (
  <div className="px-5 pt-3">
    <p className="text-[15px] font-medium text-muted-foreground">{kicker}</p>
    <h3 className="mt-0.5 text-[34px] leading-[1.1] font-bold tracking-[-0.03em]">{title}</h3>
  </div>
);

const DAY = [
  { id: "a", time: "07:00", who: () => m.features_sample_client_full(), what: () => m.features_sample_session(), done: true },
  { id: "b", time: "12:00", who: () => m.features_sample_client_three(), what: () => m.features_sample_consult(), done: true },
  { id: "c", time: "17:00", who: () => m.features_sample_class(), what: () => m.features_seats({ taken: 6, total: 8 }), next: true },
  { id: "d", time: "18:30", who: () => m.features_sample_client_two(), what: () => m.features_sample_session() },
];

/** The coach's day: who comes when, the next one lit, and the money the day brings in. */
export const TodayScreen = () => (
  <div className={screen}>
    <LargeTitle kicker={m.features_today_kicker({ count: 4 })} title={m.features_today_title()} />
    <ul className="mt-5 grid gap-2 px-4">
      {DAY.map((entry) => (
        <li
          key={entry.id}
          className={cn(
            "flex items-center gap-3.5 rounded-[22px] p-4",
            entry.next ? "bg-primary text-primary-foreground" : well,
            entry.done && "opacity-55",
          )}
        >
          <b className="w-[52px] text-[17px] font-semibold tabular-nums">{entry.time}</b>
          <span className="min-w-0 flex-1 leading-tight">
            <b className="block truncate text-[17px] font-semibold">{entry.who()}</b>
            <small className={cn("text-[14px]", entry.next ? "opacity-70" : "text-muted-foreground")}>{entry.what()}</small>
          </span>
          {entry.done ? <Check aria-hidden className="size-5 text-primary" strokeWidth={3} /> : null}
          {entry.next ? (
            <span className="rounded-full bg-primary-foreground/12 px-2.5 py-1 text-[13px] font-semibold whitespace-nowrap">
              {m.features_today_in({ minutes: 25 })}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
    <div className="mx-4 mt-3 grid grid-cols-2 gap-3">
      {[
        { id: "today", label: m.features_today_earned(), value: m.features_money_today() },
        { id: "collect", label: m.features_to_collect(), value: m.features_money_collect() },
      ].map((tile) => (
        <div key={tile.id} className={cn("rounded-[22px] p-4", well)}>
          <p className="text-[13px] text-muted-foreground">{tile.label}</p>
          <p className="mt-1 text-[22px] font-semibold tabular-nums">{tile.value}</p>
        </div>
      ))}
    </div>
  </div>
);

/** One client on one page: their pack, their plan, the next visit and how their training goes. */
export const ClientScreen = () => (
  <div className={screen}>
    <div className="flex items-center px-4 pt-1">
      <span className={cn("grid size-[38px] place-items-center rounded-full", well)}>
        <ChevronLeft aria-hidden className="size-5" />
      </span>
    </div>
    <div className="flex items-center gap-4 px-5 pt-4">
      <Initials name={m.features_sample_client_full()} className="size-[64px] rounded-[22px] text-[22px]" />
      <span>
        <b className="block text-[26px] leading-tight font-bold tracking-[-0.02em]">{m.features_sample_client_full()}</b>
        <small className="text-[15px] text-muted-foreground">{m.features_client_since()}</small>
      </span>
    </div>
    <div className="mx-4 mt-5 rounded-[22px] bg-primary p-4 text-primary-foreground">
      <p className="text-[13px] font-semibold opacity-70">{m.features_client_next()}</p>
      <p className="mt-1 text-[20px] font-semibold tracking-[-0.01em]">{m.features_client_next_when()}</p>
    </div>
    <div className={cn("mx-4 mt-3 rounded-[22px] p-4", well)}>
      <div className="flex items-baseline justify-between">
        <b className="text-[17px] font-semibold">{m.features_pack_name()}</b>
        <span className="text-[15px] text-muted-foreground tabular-nums">{m.features_pack_left({ left: 6, total: 10 })}</span>
      </div>
      <span className="mt-3 grid grid-cols-10 gap-1">
        {Array.from({ length: 10 }, (_, index) => (
          <i key={index} className={cn("h-2 rounded-full", index < 4 ? "bg-foreground/15" : "bg-primary")} />
        ))}
      </span>
    </div>
    <ul className="mt-3 grid gap-2 px-4">
      {[
        { id: "plan", icon: Dumbbell, label: m.features_plan_name(), value: m.features_plan_week({ n: 3 }) },
        { id: "best", icon: TrendingUp, label: m.features_ex_squat(), value: `${kilos(92.5)} kg` },
      ].map((row) => (
        <li key={row.id} className={cn("flex h-[58px] items-center gap-3 rounded-[18px] px-4", well)}>
          <row.icon aria-hidden className="size-5 text-primary" />
          <b className="flex-1 truncate text-[16px] font-semibold">{row.label}</b>
          <span className="text-[15px] font-semibold text-muted-foreground tabular-nums">{row.value}</span>
        </li>
      ))}
    </ul>
  </div>
);

const WEEKS = [2860, 3120, 2740, 3760];

/** The month's takings, week by week, and what clients still owe. */
export const FinanceScreen = () => {
  const top = Math.max(...WEEKS);
  return (
    <div className={screen}>
      <LargeTitle kicker={m.features_finance_month()} title={m.features_finance_title()} />
      <div className="px-5 pt-5">
        <p className="text-[15px] text-muted-foreground">{m.features_finance_takings()}</p>
        <p className="mt-1 flex items-baseline gap-2.5">
          <b className="text-[44px] leading-none font-bold tracking-[-0.04em] tabular-nums">{m.features_money_month()}</b>
          <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[14px] font-semibold text-primary">+18%</span>
        </p>
      </div>
      <div className="mx-4 mt-6 flex h-[180px] items-end gap-3 rounded-[24px] px-4 pt-5 pb-4">
        {WEEKS.map((value, index) => (
          <span key={value} className="flex h-full flex-1 flex-col justify-end gap-2 text-center">
            <span
              className={cn("rounded-[12px]", index === WEEKS.length - 1 ? "bg-primary" : "bg-foreground/12")}
              style={{ height: `${(value / top) * 100}%` }}
            />
            <small className="text-[12px] text-muted-foreground">{m.features_plan_week({ n: index + 1 })}</small>
          </span>
        ))}
      </div>
      <ul className="mt-2 grid gap-2 px-4">
        {[
          { id: "collect", label: m.features_to_collect(), detail: m.features_finance_collect_who({ count: 3 }), value: m.features_money_collect() },
          { id: "packs", label: m.features_finance_packs(), detail: m.features_finance_packs_detail(), value: "7" },
        ].map((row) => (
          <li key={row.id} className={cn("flex h-[62px] items-center gap-3 rounded-[18px] px-4", well)}>
            <span className="min-w-0 flex-1 leading-tight">
              <b className="block text-[16px] font-semibold">{row.label}</b>
              <small className="text-[13px] text-muted-foreground">{row.detail}</small>
            </span>
            <b className="text-[17px] font-semibold tabular-nums">{row.value}</b>
          </li>
        ))}
      </ul>
    </div>
  );
};

const EVENTS = [
  { id: "mobility", name: () => m.features_sample_class(), when: () => m.features_event_when_one(), taken: 6, total: 8 },
  { id: "run", name: () => m.features_event_two(), when: () => m.features_event_when_two(), taken: 11, total: 12 },
];

/** Group classes and workshops with seats, filling up. */
export const EventsScreen = () => (
  <div className={screen}>
    <LargeTitle kicker={m.features_events_kicker()} title={m.features_events_title()} />
    <ul className="mt-5 grid gap-3 px-4">
      {EVENTS.map((event) => (
        <li key={event.id} className={cn("rounded-[24px] p-4", well)}>
          <div className="flex items-start justify-between gap-3">
            <span className="leading-tight">
              <b className="block text-[19px] font-semibold tracking-[-0.01em]">{event.name()}</b>
              <small className="mt-1 flex items-center gap-1.5 text-[14px] text-muted-foreground">
                <MapPin aria-hidden className="size-3.5" />
                {event.when()}
              </small>
            </span>
            <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[13px] font-semibold text-primary tabular-nums">
              {m.features_seats({ taken: event.taken, total: event.total })}
            </span>
          </div>
          <span className="mt-4 flex items-center gap-3">
            <span className="flex -space-x-2">
              {["AK", "MZ", "JW", "PL"].map((initials) => (
                <span
                  key={initials}
                  className="grid size-8 place-items-center rounded-full bg-primary/20 text-[11px] font-semibold text-primary ring-2 ring-background"
                >
                  {initials}
                </span>
              ))}
            </span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-foreground/10">
              <span className="block h-full rounded-full bg-primary" style={{ width: `${(event.taken / event.total) * 100}%` }} />
            </span>
          </span>
        </li>
      ))}
      <li className="flex h-[54px] items-center justify-center gap-2 rounded-[20px] border border-dashed border-foreground/15 text-[15px] text-muted-foreground">
        <Plus aria-hidden className="size-4" />
        {m.features_events_new()}
      </li>
    </ul>
  </div>
);

const CLIENT_PLAN = [
  { id: "squat", name: () => m.features_ex_squat(), sets: "4 × 6", weight: 85 },
  { id: "rdl", name: () => m.features_ex_rdl(), sets: "3 × 8", weight: 70 },
  { id: "lunge", name: () => m.features_ex_lunge(), sets: "3 × 10", weight: 16 },
];

/** The client's phone, in the athlete app: the plan the coach just sent, ready to start. */
export const ClientPlanScreen = () => (
  <div style={athleteTheme} className={screen}>
    <LargeTitle kicker={m.features_from_coach({ name: m.features_sample_coach() })} title={m.features_plan_day_a()} />
    <p className="px-5 pt-1 text-[15px] text-muted-foreground">{m.features_plan_name()}</p>
    <ul className="mt-5 grid gap-2 px-4">
      {CLIENT_PLAN.map((exercise) => (
        <li key={exercise.id} className={cn("flex items-center gap-3 rounded-[20px] p-3", well)}>
          <span className="grid size-[46px] place-items-center rounded-[14px] bg-foreground/[0.08]">
            <Dumbbell aria-hidden className="size-5 text-muted-foreground" />
          </span>
          <span className="min-w-0 flex-1 leading-tight">
            <b className="block truncate text-[16px] font-semibold">{exercise.name()}</b>
            <small className="text-[14px] text-muted-foreground tabular-nums">{exercise.sets}</small>
          </span>
          <b className="text-[15px] font-semibold tabular-nums">{kilos(exercise.weight)} kg</b>
        </li>
      ))}
    </ul>
    <span className="mx-4 mt-5 flex h-[58px] items-center justify-center rounded-full bg-primary text-[17px] font-semibold text-primary-foreground">
      {m.features_start_workout()}
    </span>
  </div>
);

/** The client's phone during a coached training: the plan filling in as the coach types. */
export const ClientLiveScreen = () => (
  <div style={athleteTheme} className={screen}>
    <div className="px-5 pt-3">
      <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-3 py-1 text-[13px] font-semibold text-primary">
        <i className="size-2 animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
        {m.features_live_badge()}
      </span>
      <h3 className="mt-3 text-[30px] leading-[1.1] font-bold tracking-[-0.03em]">{m.features_live_title_short()}</h3>
    </div>
    <ul className="mt-5 grid gap-2 px-4">
      {[
        { id: "squat", name: () => m.features_ex_squat(), detail: `4 × 8 · ${kilos(82.5)} kg`, done: "2/4" },
        { id: "row", name: () => m.features_ex_row(), detail: `3 × 10 · ${kilos(60)} kg` },
        { id: "press", name: () => m.features_ex_press(), detail: `3 × 10 · ${kilos(26)} kg`, fresh: true },
      ].map((exercise) => (
        <li
          key={exercise.id}
          className={cn(
            "flex items-center gap-3 rounded-[20px] p-3",
            well,
            exercise.fresh && "animate-pulse ring-1 ring-primary/50 ring-inset motion-reduce:animate-none",
          )}
        >
          <span className="grid size-[46px] place-items-center rounded-[14px] bg-foreground/[0.08]">
            <Dumbbell aria-hidden className="size-5 text-muted-foreground" />
          </span>
          <span className="min-w-0 flex-1 leading-tight">
            <b className="block truncate text-[16px] font-semibold">{exercise.name()}</b>
            <small className="text-[14px] text-muted-foreground tabular-nums">{exercise.detail}</small>
          </span>
          {exercise.done ? <span className="text-[14px] font-semibold text-primary tabular-nums">{exercise.done}</span> : null}
        </li>
      ))}
    </ul>
  </div>
);

const SERVICES = [
  { id: "session", name: () => m.features_sample_session(), detail: () => m.start_page_service_session(), price: () => m.features_money_session() },
  { id: "pack", name: () => m.features_pack_name(), detail: () => m.start_page_service_pack(), price: () => m.start_page_money_pack() },
];

/**
 * The page a client opens from the link, in a phone's browser: who the coach is, what they offer
 * and today's free times. It lives in the athlete app, so it wears the athlete app's lime.
 */
export const PageScreen = () => (
  <div style={athleteTheme} className="absolute inset-0 flex flex-col pt-[54px]">
    <div className="relative mx-3 overflow-hidden rounded-[30px] bg-[linear-gradient(160deg,color-mix(in_oklch,var(--primary)_55%,black),oklch(0.2_0.02_120))] px-5 pt-6 pb-5">
      <span className="absolute -top-16 -right-10 size-48 rounded-full bg-primary/40 blur-3xl" />
      <Initials
        name={m.features_sample_coach_full()}
        className="relative size-[64px] rounded-[22px] bg-background/30 from-transparent to-transparent text-[22px] text-white backdrop-blur-md"
      />
      <p className="relative mt-4 text-[26px] leading-tight font-bold tracking-[-0.02em] text-white">
        {m.features_sample_coach_full()}
      </p>
      <p className="relative mt-1 text-[15px] leading-snug text-white/75">{m.start_page_headline()}</p>
    </div>
    <ul className="mt-4 grid gap-2 px-4">
      {SERVICES.map((service) => (
        <li key={service.id} className={cn("flex items-center gap-3 rounded-[20px] px-4 py-3.5", well)}>
          <span className="min-w-0 flex-1 leading-tight">
            <b className="block text-[17px] font-semibold">{service.name()}</b>
            <small className="text-[14px] text-muted-foreground">{service.detail()}</small>
          </span>
          <b className="text-[17px] font-semibold tabular-nums">{service.price()}</b>
        </li>
      ))}
    </ul>
    <p className="mt-5 px-5 text-[13px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">
      {m.start_page_free_today()}
    </p>
    <div className="mt-2 grid grid-cols-3 gap-2 px-4">
      {["17:30", "18:30", "19:30"].map((time, index) => (
        <span
          key={time}
          className={cn(
            "grid h-[48px] place-items-center rounded-[16px] text-[16px] font-semibold tabular-nums",
            index === 0 ? "bg-primary text-primary-foreground" : well,
          )}
        >
          {time}
        </span>
      ))}
    </div>
    <div className="absolute inset-x-3 bottom-[30px] flex h-[50px] items-center gap-2 rounded-full bg-foreground/[0.09] px-4 text-[15px] backdrop-blur-xl">
      <Lock aria-hidden className="size-3.5 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-center font-medium">{m.start_page_link()}</span>
      <RotateCw aria-hidden className="size-4 text-muted-foreground" />
    </div>
  </div>
);

const bubble = "max-w-[78%] rounded-[22px] px-4 py-2.5 text-[16px] leading-snug";

/** A message thread on the coach's phone: a client asks, the coach sends the link, the client books. */
export const LinkScreen = () => (
  <div className={screen}>
    <div className="flex flex-col items-center border-b border-foreground/[0.06] pb-3">
      <Initials name={m.features_sample_client_full()} className="size-[52px] rounded-full text-[18px]" />
      <b className="mt-1.5 text-[13px] font-semibold">{m.features_sample_client_full()}</b>
    </div>
    <div className="flex flex-col gap-2 px-4 pt-4">
      <p className={cn(bubble, "self-start rounded-bl-[8px] bg-foreground/[0.1]")}>{m.start_page_chat_ask()}</p>
      <p className={cn(bubble, "self-end rounded-br-[8px] bg-primary text-primary-foreground")}>
        {m.start_page_chat_reply()}
      </p>
      <div className="w-[78%] self-end overflow-hidden rounded-[22px] rounded-br-[8px] bg-foreground/[0.1]">
        <span className="grid h-[120px] place-items-center bg-[linear-gradient(150deg,oklch(0.55_0.17_128),oklch(0.25_0.04_125))]">
          <BrandMark className="size-12 rounded-[14px] bg-white/90 text-[oklch(0.25_0.04_125)]" />
        </span>
        <span className="block px-4 py-3 leading-tight">
          <b className="block truncate text-[15px] font-semibold">{m.start_page_preview_title()}</b>
          <small className="text-[13px] text-muted-foreground">{m.start_page_preview_domain()}</small>
        </span>
      </div>
      <p className={cn(bubble, "mt-2 self-start rounded-bl-[8px] bg-foreground/[0.1]")}>{m.start_page_chat_booked()}</p>
      <span className="mx-auto mt-3 inline-flex items-center gap-2 rounded-full bg-primary/15 px-3.5 py-1.5 text-[13px] font-semibold text-primary">
        <Check aria-hidden className="size-3.5" strokeWidth={3} />
        {m.features_hero_chip_booked({ name: m.features_sample_client_first(), time: "18:30" })}
      </span>
    </div>
  </div>
);

const MEALS = [
  { id: "breakfast", name: () => m.for_whom_meal_breakfast(), dish: () => m.for_whom_meal_breakfast_dish(), kcal: 520, split: [24, 56, 20] },
  { id: "lunch", name: () => m.for_whom_meal_lunch(), dish: () => m.for_whom_meal_lunch_dish(), kcal: 740, split: [34, 46, 20] },
  { id: "dinner", name: () => m.for_whom_meal_dinner(), dish: () => m.for_whom_meal_dinner_dish(), kcal: 610, split: [30, 38, 32] },
];

const MACRO_TONES = ["bg-primary", "bg-foreground/45", "bg-foreground/20"];

/** A diet plan being written for a client: the day's target, then each meal with its macros. */
export const DietPlanScreen = () => (
  <div className={screen}>
    <LargeTitle kicker={m.features_sample_client_full()} title={m.for_whom_diet_title()} />
    <div className="mx-4 mt-5 rounded-[24px] bg-primary p-4 text-primary-foreground">
      <p className="text-[13px] font-semibold opacity-70">{m.for_whom_diet_target()}</p>
      <p className="mt-1 text-[30px] leading-none font-bold tracking-[-0.03em] tabular-nums">{m.for_whom_diet_kcal()}</p>
      <p className="mt-2 text-[14px] font-medium opacity-75 tabular-nums">{m.for_whom_diet_macros()}</p>
    </div>
    <ul className="mt-3 grid gap-2 px-4">
      {MEALS.map((meal) => (
        <li key={meal.id} className={cn("rounded-[20px] p-4", well)}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 leading-tight">
              <small className="text-[13px] text-muted-foreground">{meal.name()}</small>
              <b className="block truncate text-[17px] font-semibold">{meal.dish()}</b>
            </span>
            <b className="text-[16px] font-semibold tabular-nums">{meal.kcal} kcal</b>
          </div>
          <span className="mt-3 flex h-2 gap-1 overflow-hidden rounded-full">
            {meal.split.map((share, index) => (
              <i key={MACRO_TONES[index]} className={cn("rounded-full", MACRO_TONES[index])} style={{ width: `${share}%` }} />
            ))}
          </span>
        </li>
      ))}
    </ul>
  </div>
);
